// Phase 10B.3 — SIKA plan calculator. Pure deterministic math.
//
// This module is intentionally I/O-free: no Supabase reads, no
// network calls, no Date.now(), no localStorage, no agents. Inputs
// are passed in; outputs are derived. The calculator powers the
// /sika scenario panel and is the precondition for any future
// activation surface, but it does NOT activate, does NOT write to
// rollover_chains, and does NOT promise outcomes.
//
// Honesty framework (blueprint §7.4):
//   - probability is shown for every scenario
//   - downside is shown for every scenario
//   - language is "could reach", never "will reach"
//   - infeasible scenarios are surfaced as such, not hidden
//
// Math model (single-leg compounding rollover with constant takeout):
//   Let r = targetOdds * (1 - takeoutRate)   [stake-side multiplier]
//   Let h = targetOdds * takeoutRate         [withdraw-side multiplier]
//   stake_{k+1} = stake_k * r
//   withdrawn_k = stake_k * h
//   running_protected_k = sum_{i=1..k} withdrawn_i
//   goal hits at smallest k where running_protected_k >= targetAmount
//   chain probability of reaching week k = winProbabilityPerWeek ^ k
//
// "protectedAmountAtBust" is the running protected accumulated
// through (estimatedWeeks - 1) successful weeks — i.e., what would
// already be safe if the chain busts in the very final week.

export type SikaScenarioRoute = "conservative" | "moderate" | "aggressive";

export interface SikaPlanInput {
  readonly targetAmount: number;
  readonly currentAmount?: number;
  readonly openingStake: number;
  readonly targetOdds: number;
  readonly maxWeeks: number;
  readonly takeoutRate: number;
  readonly winProbabilityPerWeek: number;
}

export interface SikaPlanWeekRow {
  readonly week: number;
  readonly startingStake: number;
  readonly grossReturn: number;
  readonly protectedAmount: number;
  readonly nextStake: number;
  readonly runningProtected: number;
  /** Effective takeout rate used this week (may differ from base due to dynamic ramp). */
  readonly takeoutRate: number;
}

export interface SikaPlanScenario {
  readonly route: SikaScenarioRoute;
  readonly targetAmount: number;
  readonly openingStake: number;
  readonly targetOdds: number;
  readonly takeoutRate: number;
  readonly maxWeeks: number;
  readonly estimatedWeeks: number | null;
  readonly fullChainProbability: number;
  readonly projectedFinalAmount: number;
  readonly protectedAmountAtBust: number;
  readonly downsideSummary: string;
  readonly feasible: boolean;
  readonly warnings: readonly string[];
  readonly weekRows: readonly SikaPlanWeekRow[];
}

// Planning assumptions only. Not historical performance. Not a
// promise. Editable by future phases when real settled data exists.
//
// Takeout rates are tuned for compounding:
//   Conservative 2.0x → 30% takeout, 70% re-stake (r = 1.40)
//     At 50% takeout, r = 1.0 — stake is FLAT, zero compounding.
//     30% takeout gives meaningful growth while still extracting value.
//   Moderate 2.5x → 40% takeout, 60% re-stake (r = 1.50)
//     Good balance of compounding and protection.
//   Aggressive 3.0x → 30% takeout, 70% re-stake (r = 2.10)
//     Maximum compounding — higher risk, faster growth.
//
// As bankroll grows (running protected approaches target), the
// dynamic takeout system (computeDynamicTakeout) gradually increases
// withdrawal rates toward 50% to lock in gains. See calculateSikaPlan.
//
// Win probabilities per week are based on fair odds minus bookmaker margin:
//   Conservative 2.0x → fair 50%, with ~2% vig → 0.48
//   Moderate 2.5x → fair 40%, with ~2% vig → 0.38
//   Aggressive 3.0x → fair 33%, with ~1% vig → 0.32
export const SIKA_DEFAULT_ASSUMPTIONS: Record<
  SikaScenarioRoute,
  { targetOdds: number; takeoutRate: number; winProbabilityPerWeek: number }
> = {
  conservative: { targetOdds: 2.0, takeoutRate: 0.30, winProbabilityPerWeek: 0.48 },
  moderate: { targetOdds: 2.5, takeoutRate: 0.40, winProbabilityPerWeek: 0.38 },
  aggressive: { targetOdds: 3.0, takeoutRate: 0.30, winProbabilityPerWeek: 0.32 },
};

/**
 * Compute dynamic takeout rate based on progress toward target.
 *
 * Strategy: Start with the base takeout rate (e.g. 30% for conservative).
 * As running protected amount approaches the target, gradually increase
 * takeout toward 50% to lock in gains and reduce exposure.
 *
 * Phases:
 *   0-50% of target  → use base takeout (maximize compounding)
 *   50-80% of target  → linearly ramp from base toward 50%
 *   80-100% of target → 50% takeout (protect the lead)
 */
export function computeDynamicTakeout(
  baseTakeout: number,
  runningProtected: number,
  targetAmount: number,
): number {
  if (targetAmount <= 0) return baseTakeout;
  const progress = runningProtected / targetAmount;

  if (progress < 0.50) return baseTakeout;
  if (progress >= 0.80) return 0.50;

  // Linear ramp from base to 0.50 between 50% and 80% progress
  const rampProgress = (progress - 0.50) / 0.30; // 0 at 50%, 1 at 80%
  return baseTakeout + (0.50 - baseTakeout) * rampProgress;
}

export class SikaPlanInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SikaPlanInputError";
  }
}

function clamp01(p: number): number {
  if (!Number.isFinite(p)) return 0;
  if (p < 0) return 0;
  if (p > 1) return 1;
  return p;
}

function validate(input: SikaPlanInput): readonly string[] {
  const w: string[] = [];
  if (!Number.isFinite(input.targetAmount) || input.targetAmount <= 0)
    throw new SikaPlanInputError("targetAmount must be > 0");
  if (!Number.isFinite(input.openingStake) || input.openingStake <= 0)
    throw new SikaPlanInputError("openingStake must be > 0");
  if (!Number.isFinite(input.targetOdds) || input.targetOdds <= 1)
    throw new SikaPlanInputError("targetOdds must be > 1");
  if (!Number.isFinite(input.takeoutRate) || input.takeoutRate < 0 || input.takeoutRate >= 1)
    throw new SikaPlanInputError("takeoutRate must be in [0, 1)");
  if (!Number.isFinite(input.maxWeeks) || input.maxWeeks <= 0 || !Number.isInteger(input.maxWeeks))
    throw new SikaPlanInputError("maxWeeks must be a positive integer");
  if (
    !Number.isFinite(input.winProbabilityPerWeek) ||
    input.winProbabilityPerWeek <= 0 ||
    input.winProbabilityPerWeek > 1
  )
    throw new SikaPlanInputError("winProbabilityPerWeek must be in (0, 1]");
  if (input.currentAmount !== undefined && input.currentAmount > input.targetAmount) {
    w.push("Current amount already at or above target.");
  }
  return w;
}

export function calculateSikaPlan(
  input: SikaPlanInput,
  route: SikaScenarioRoute,
): SikaPlanScenario {
  const warnings = [...validate(input)];

  const seed = Math.max(0, input.currentAmount ?? 0);
  const remaining = Math.max(0, input.targetAmount - seed);

  const rows: SikaPlanWeekRow[] = [];
  let stake = input.openingStake;
  let runningProtected = seed;
  let estimatedWeeks: number | null = null;

  for (let week = 1; week <= input.maxWeeks; week += 1) {
    // Dynamic takeout: starts at base rate, ramps toward 50% as we approach the target
    const effectiveTakeout = computeDynamicTakeout(input.takeoutRate, runningProtected, input.targetAmount);
    const r = input.targetOdds * (1 - effectiveTakeout);
    const h = input.targetOdds * effectiveTakeout;

    const grossReturn = stake * input.targetOdds;
    const protectedAmount = stake * h;
    const nextStake = stake * r;
    runningProtected += protectedAmount;
    rows.push({
      week,
      startingStake: stake,
      grossReturn,
      protectedAmount,
      nextStake,
      runningProtected,
      takeoutRate: effectiveTakeout,
    });
    if (runningProtected >= input.targetAmount && estimatedWeeks === null) {
      estimatedWeeks = week;
      break;
    }
    stake = nextStake;
  }

  const feasible = estimatedWeeks !== null;
  const fullChainProbability =
    estimatedWeeks !== null
      ? clamp01(Math.pow(input.winProbabilityPerWeek, estimatedWeeks))
      : clamp01(Math.pow(input.winProbabilityPerWeek, input.maxWeeks));

  const projectedFinalAmount =
    rows.length > 0 ? (rows[rows.length - 1]?.runningProtected ?? seed) : seed;
  const protectedAmountAtBust =
    rows.length >= 2 ? (rows[rows.length - 2]?.runningProtected ?? seed) : seed;

  const downsideSummary = feasible
    ? `If the chain busts in the final week, ${protectedAmountAtBust.toFixed(2)} is already protected and survives. Remaining ${remaining.toFixed(2)} would be unrecovered.`
    : `Within ${String(input.maxWeeks)} weeks this route does not reach the target. Increase the timeline, lower the target, or reduce the risk assumption.`;

  if (!feasible) warnings.push("infeasible_within_max_weeks");

  return {
    route,
    targetAmount: input.targetAmount,
    openingStake: input.openingStake,
    targetOdds: input.targetOdds,
    takeoutRate: input.takeoutRate,
    maxWeeks: input.maxWeeks,
    estimatedWeeks,
    fullChainProbability,
    projectedFinalAmount,
    protectedAmountAtBust,
    downsideSummary,
    feasible,
    warnings,
    weekRows: rows,
  };
}

export interface SikaScenarioInput {
  readonly targetAmount: number;
  readonly currentAmount?: number;
  readonly openingStake: number;
  readonly maxWeeks: number;
  /** Override default takeout rate for all routes (from risk_tolerance). */
  readonly takeoutRate?: number;
}

// Phase B.4 — Multi-chain goal plan calculator (Pro+ only).
// Splits the target across N parallel chains, each with its own
// odds/market. Combined probability is the product of per-chain
// probabilities. Used for Category B (major purchases) and E (recurring).

export interface MultiChainInput {
  readonly targetAmount: number;
  readonly currentAmount?: number;
  readonly chains: readonly {
    readonly label: string;
    readonly openingStake: number;
    readonly targetOdds: number;
    readonly takeoutRate: number;
    readonly winProbabilityPerWeek: number;
    readonly maxWeeks: number;
    readonly market: string;
  }[];
}

export interface MultiChainResult {
  readonly targetAmount: number;
  readonly chainCount: number;
  readonly chains: readonly (SikaPlanScenario & { label: string; market: string })[];
  readonly combinedProbability: number;
  readonly totalProjected: number;
  readonly totalProtectedAtBust: number;
  readonly feasible: boolean;
  readonly downsideSummary: string;
}

export function calculateMultiChainPlan(input: MultiChainInput): MultiChainResult {
  if (input.chains.length === 0) {
    throw new SikaPlanInputError("At least one chain is required");
  }
  if (input.chains.length > 5) {
    throw new SikaPlanInputError("Maximum 5 parallel chains");
  }

  const sharePerChain = input.targetAmount / input.chains.length;
  const currentPerChain = (input.currentAmount ?? 0) / input.chains.length;

  const results = input.chains.map((c) => {
    const plan = calculateSikaPlan(
      {
        targetAmount: sharePerChain,
        currentAmount: currentPerChain,
        openingStake: c.openingStake,
        targetOdds: c.targetOdds,
        takeoutRate: c.takeoutRate,
        winProbabilityPerWeek: c.winProbabilityPerWeek,
        maxWeeks: c.maxWeeks,
      },
      "moderate",
    );
    return { ...plan, label: c.label, market: c.market };
  });

  const combinedProbability = results.reduce((p, r) => p * r.fullChainProbability, 1);
  const totalProjected = results.reduce((s, r) => s + r.projectedFinalAmount, 0);
  const totalProtectedAtBust = results.reduce((s, r) => s + r.protectedAmountAtBust, 0);
  const feasible = results.every((r) => r.feasible);

  const downsideSummary = feasible
    ? `If any chain busts, ${totalProtectedAtBust.toFixed(2)} is protected across all chains. Combined probability of all chains completing: ${(combinedProbability * 100).toFixed(1)}%.`
    : `One or more chains cannot reach their share of the target within the given timeline. Consider adjusting stakes or extending the window.`;

  return {
    targetAmount: input.targetAmount,
    chainCount: input.chains.length,
    chains: results,
    combinedProbability,
    totalProjected,
    totalProtectedAtBust,
    feasible,
    downsideSummary,
  };
}

export function calculateSikaScenarios(
  input: SikaScenarioInput,
): Record<SikaScenarioRoute, SikaPlanScenario> {
  const routes: SikaScenarioRoute[] = ["conservative", "moderate", "aggressive"];
  const out = {} as Record<SikaScenarioRoute, SikaPlanScenario>;
  for (const route of routes) {
    const a = SIKA_DEFAULT_ASSUMPTIONS[route];
    out[route] = calculateSikaPlan(
      {
        ...input,
        targetOdds: a.targetOdds,
        takeoutRate: input.takeoutRate ?? a.takeoutRate,
        winProbabilityPerWeek: a.winProbabilityPerWeek,
      },
      route,
    );
  }
  return out;
}
